import { ObjectServiceExt } from "src/businessServices/businessServises.types";
import { ZAttribute } from "src/types/ZAttribute";
import { ZUid } from "src/types/zUid";
import { CommonNodeO2 } from "../../Obj2Tab/Obj2Nodes";
import { objectFields } from "../kinds/object";
import { ownerGroupOf } from "../kinds/value";
import {
  dispatch,
  nodeExpandable,
  nodeIcon,
  nodeOwnActions,
  nodeTitle,
} from "../kinds/registry";
import { KindContext } from "../kinds/types";
import { NodeId } from "../model/node";
import {
  NodeActions,
  allowedActions,
  childActionsOf,
  mergeActions,
} from "../model/permissions";

/**
 * Проекция домена в узлы, которых ждут Ant Tree и формы вкладки.
 *
 * Это не сужение типов и не приведение: чистая функция «домен → DTO», которая
 * строит ровно `ObjectO2 | GroupO2 | AttributeO2 | ValueO2` из v1. Формы не
 * переписываются, а домен про них не знает — граница проходит здесь.
 */
export type ProjectionContext = KindContext & {
  children(id: NodeId): readonly NodeId[] | undefined;
  /** Подключённые бизнес-сервисы: из них экран объекта строит вкладки. */
  services(objectId: ZUid): ObjectServiceExt[] | undefined;
  /** Полный список атрибутов объекта: его ждут форма и копирование. */
  attributes(objectId: ZUid): ZAttribute[] | undefined;
};

const groupIdOf = (id: NodeId, ctx: ProjectionContext): ZUid => {
  const node = ctx.node(id);
  if (node?.kind !== "value") throw Error(`Узел ${id} не является значением`);
  return ownerGroupOf(node, ctx).data.id;
};

export const projectNode = (
  id: NodeId,
  ownerActions: NodeActions,
  ctx: ProjectionContext,
): CommonNodeO2 | null => {
  const node = ctx.node(id);
  if (!node) return null;

  const actions = mergeActions(ownerActions, nodeOwnActions(node, ctx));
  const inherited = node.kind === "object" ? childActionsOf(actions) : actions;
  const childIds = ctx.children(id);
  const children = childIds
    ?.map((childId) => projectNode(childId, inherited, ctx))
    .filter((child): child is CommonNodeO2 => !!child);
  const base = {
    key: node.id,
    title: nodeTitle(node, actions),
    icon: nodeIcon(node),
    children,
    // Дети не загружены — узел раскрываемый; загружены и пусты — лист
    isLeaf: children ? children.length === 0 : !nodeExpandable(node),
    actions,
  };

  return dispatch<CommonNodeO2>(node, {
    object: (it) => ({
      ...base,
      type: "obj",
      object: objectFields(it, ctx),
      services: ctx.services(it.data.id),
      attrList: ctx.attributes(it.data.id),
    }),
    group: (it) => ({ ...base, type: "group", group: it.data }),
    attribute: (it) => ({
      ...base,
      type: "attr",
      attr: it.data,
      // Состояния объекта-владельца: по ним форма строит таблицу прав атрибута
      states: ctx.states(ctx.ownerObject(it.id)?.data.id ?? it.data.id),
    }),
    value: (it) => ({
      ...base,
      type: "value",
      value: it.data,
      groupId: groupIdOf(it.id, ctx),
      // Поле обязательно в типе v1, но его никто не читает: загрузку вложений
      // ведёт дерево, а не сам узел
      things: { status: "none" },
    }),
  });
};

export const projectRoots = (
  rootIds: readonly NodeId[],
  ownerActions: NodeActions,
  ctx: ProjectionContext,
): CommonNodeO2[] =>
  rootIds
    .map((id) => projectNode(id, ownerActions, ctx))
    .filter((node): node is CommonNodeO2 => !!node);

/**
 * Проекция одного узла вне дерева.
 * Права владельца вычисляются по цепочке вверх: узел должен выглядеть так же,
 * как он выглядит внутри дерева.
 */
export const projectSingleNode = (
  id: NodeId,
  ctx: ProjectionContext,
): CommonNodeO2 | null => {
  const ownerActionsOf = (nodeId: NodeId): NodeActions => {
    const node = ctx.node(nodeId);
    const ownerId = node && node.kind !== "object" ? node.ownerId : null;
    const owner = ownerId ? ctx.node(ownerId) : undefined;
    if (!owner) return allowedActions;
    const ownerActions = mergeActions(
      ownerActionsOf(owner.id),
      nodeOwnActions(owner, ctx),
    );
    return owner.kind === "object"
      ? childActionsOf(ownerActions)
      : ownerActions;
  };
  return projectNode(id, ownerActionsOf(id), ctx);
};
